import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { correctiveActionApi } from "../../services/correctiveActionApi";
import { usePermissions } from "../../hooks/usePermissions";
import { StatusBadge } from "../../components/StatusBadge";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { formatApiErrorDetail } from "../../lib/api";
import { statusLabel } from "../../lib/statusLabel";
import { NotFoundState } from "../../components/NotFoundState";
import { useLocalization } from "../../hooks/useLocalization";

const ALLOWED_TRANSITIONS = {
  OPEN: ["PLAN_REQUIRED", "IN_PROGRESS"],
  PLAN_REQUIRED: ["IN_PROGRESS"],
  IN_PROGRESS: ["EVIDENCE_PENDING", "MANAGEMENT_REVIEW"],
  EVIDENCE_PENDING: ["MANAGEMENT_REVIEW", "IN_PROGRESS"],
  MANAGEMENT_REVIEW: ["IN_PROGRESS"],
  READY_TO_SUBMIT: [],
  SUBMITTED: ["FOLLOW_UP_REQUIRED", "CLOSED"],
  FOLLOW_UP_REQUIRED: ["CLOSED", "IN_PROGRESS"],
  OVERDUE: ["IN_PROGRESS", "EVIDENCE_PENDING", "MANAGEMENT_REVIEW", "READY_TO_SUBMIT", "SUBMITTED", "CLOSED"],
};
const EDITABLE_FIELDS = ["root_cause", "required_correction", "responsible_owner", "target_date", "labor_office_section"];

export default function CorrectiveActionDetailPage() {
  const { caseId } = useParams();
  const { t } = useLocalization();
  const { canManageCorrectiveAction } = usePermissions();
  const [item, setItem] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [draft, setDraft] = useState(null);
  const [evidence, setEvidence] = useState({ label: "", storage_path: "" });
  const [followUp, setFollowUp] = useState("");
  const [approveNote, setApproveNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    correctiveActionApi.getCase(caseId).then((r) => { setItem(r.data); setDraft(r.data); setNotFound(false); }).catch((err) => {
      if (err.response?.status === 404) setNotFound(true);
    });
  }, [caseId]);
  useEffect(() => { load(); }, [load]);

  if (notFound) return <NotFoundState testId="corrective-action-not-found" backTo="/management/compliance/corrective-actions" />;
  if (!item || !draft) return <p className="text-sm text-gray-500" data-testid="ca-detail-loading">{t("common.loading")}</p>;

  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg || t("mgmtHealthCommittee.updated")); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveDraft = () => run(() => correctiveActionApi.updateCase(caseId, { ...Object.fromEntries(EDITABLE_FIELDS.map((f) => [f, draft[f]])), target_date: draft.target_date || null }), t("mgmtCorrectiveAction.caseSaved"));
  const addEvidence = () => { if (!evidence.label) return; run(() => correctiveActionApi.addEvidence(caseId, evidence.label, evidence.storage_path || null), t("mgmtCorrectiveAction.evidenceAdded")).then(() => setEvidence({ label: "", storage_path: "" })); };

  return (
    <div className="max-w-3xl space-y-5" data-testid="corrective-action-detail-page">
      <PageHelpBanner pageKey="corrective-action-detail" title={t("pageHelp.defaultTitle")}>
        <p dangerouslySetInnerHTML={{ __html: t("mgmtCorrectiveAction.detailHelp") }} />
      </PageHelpBanner>
      <Link to="/management/compliance/corrective-actions" className="text-xs font-semibold text-blue-600 hover:underline" data-testid="ca-back-link">&larr; {t("mgmtCorrectiveAction.backToList")}</Link>

      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <p className="text-xs text-gray-500 font-mono">{item.code}</p>
            <h2 className="text-xl font-bold text-gray-900" data-testid="ca-detail-title">{item.title}</h2>
          </div>
          <StatusBadge status={item.status} />
        </div>
        {item.finding && <p className="text-sm text-gray-600"><span className="text-xs text-gray-500 block">{t("mgmtCorrectiveAction.finding")}</span>{item.finding}</p>}
        {item.legal_provision && <p className="text-xs text-gray-500"><span className="text-gray-500">{t("mgmtCorrectiveAction.legalProvision")}:</span> {item.legal_provision}</p>}

        {canManageCorrectiveAction && item.status !== "CLOSED" ? (
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><label className="block text-xs text-gray-500 mb-1">{t("mgmtCorrectiveAction.rootCause")}</label>
              <textarea data-testid="ca-root-cause-input" value={draft.root_cause || ""} onChange={(e) => setDraft((d) => ({ ...d, root_cause: e.target.value }))} rows={2} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div className="col-span-2"><label className="block text-xs text-gray-500 mb-1">{t("mgmtCorrectiveAction.requiredCorrection")}</label>
              <textarea data-testid="ca-detail-required-correction-input" value={draft.required_correction || ""} onChange={(e) => setDraft((d) => ({ ...d, required_correction: e.target.value }))} rows={2} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div><label className="block text-xs text-gray-500 mb-1">{t("mgmtCorrectiveAction.responsibleOwner")}</label>
              <input data-testid="ca-detail-owner-input" value={draft.responsible_owner || ""} onChange={(e) => setDraft((d) => ({ ...d, responsible_owner: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div><label className="block text-xs text-gray-500 mb-1">{t("mgmtCorrectiveAction.colTarget")}</label>
              <input data-testid="ca-detail-target-date-input" type="date" value={draft.target_date || ""} onChange={(e) => setDraft((d) => ({ ...d, target_date: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div className="col-span-2"><label className="block text-xs text-gray-500 mb-1">{t("mgmtCorrectiveAction.laborOfficeSection")}</label>
              <input data-testid="ca-detail-labor-office-input" value={draft.labor_office_section || ""} onChange={(e) => setDraft((d) => ({ ...d, labor_office_section: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
            </div>
            <button data-testid="ca-save-btn" disabled={busy} onClick={saveDraft} className="px-4 py-2 rounded-lg text-xs font-semibold bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50 col-span-2 w-fit">{t("mgmtHealthCommittee.saveChanges")}</button>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 text-xs text-gray-500">
            <div><span className="block text-gray-500">{t("mgmtCorrectiveAction.rootCause")}</span>{item.root_cause || "-"}</div>
            <div><span className="block text-gray-500">{t("mgmtHealthCommittee.colOwner")}</span>{item.responsible_owner || "-"}</div>
            <div><span className="block text-gray-500">{t("mgmtCorrectiveAction.requiredCorrection")}</span>{item.required_correction || "-"}</div>
            <div><span className="block text-gray-500">{t("mgmtCorrectiveAction.colTarget")}</span>{item.target_date || "-"}</div>
          </div>
        )}
      </div>

      {canManageCorrectiveAction && item.status !== "CLOSED" && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3" data-testid="ca-lifecycle-panel">
          <h3 className="text-sm font-semibold text-gray-700">{t("mgmtCorrectiveAction.statusTransitions")}</h3>
          <div className="flex flex-wrap gap-2">
            {(ALLOWED_TRANSITIONS[item.status] || []).map((target) => (
              <button key={target} data-testid={`ca-transition-${target}`} disabled={busy} onClick={() => run(() => correctiveActionApi.transitionStatus(caseId, target), `${t("mgmtCorrectiveAction.movedTo")} ${target}`)} className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-200 hover:bg-gray-50 disabled:opacity-50">
                {t("mgmtCorrectiveAction.moveTo")} {statusLabel(t, target)}
              </button>
            ))}
            {item.status === "READY_TO_SUBMIT" && (
              <button data-testid="ca-submit-btn" disabled={busy} onClick={() => run(() => correctiveActionApi.submitCase(caseId), t("mgmtHealthCommittee.submitted"))} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">{t("mgmtHealthCommittee.submit")}</button>
            )}
          </div>

          {["MANAGEMENT_REVIEW", "SUBMITTED"].includes(item.status) && (
            <div className="flex flex-wrap gap-2 items-end pt-2 border-t border-gray-100">
              <input data-testid="ca-approve-note-input" value={approveNote} onChange={(e) => setApproveNote(e.target.value)} placeholder={t("mgmtCorrectiveAction.approvalNoteOptional")} className="flex-1 min-w-[180px] border border-gray-200 rounded-lg px-3 py-1.5 text-sm" />
              <button data-testid="ca-approve-btn" disabled={busy} onClick={() => run(() => correctiveActionApi.approveCase(caseId, approveNote || null), t("mgmtHealthCommittee.approved"))} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">{t("common.approve")}</button>
            </div>
          )}

          {item.status === "FOLLOW_UP_REQUIRED" && (
            <div className="flex flex-wrap gap-2 items-end pt-2 border-t border-gray-100">
              <input data-testid="ca-follow-up-input" value={followUp} onChange={(e) => setFollowUp(e.target.value)} placeholder={t("mgmtCorrectiveAction.followUpResponse")} className="flex-1 min-w-[180px] border border-gray-200 rounded-lg px-3 py-1.5 text-sm" />
              <button data-testid="ca-record-follow-up-btn" disabled={busy || !followUp} onClick={() => run(() => correctiveActionApi.recordFollowUp(caseId, followUp), t("mgmtCorrectiveAction.followUpRecorded"))} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">{t("mgmtCorrectiveAction.record")}</button>
            </div>
          )}
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
        <h3 className="text-sm font-semibold text-gray-700">{t("mgmtCorrectiveAction.evidence")}</h3>
        {canManageCorrectiveAction && item.status !== "CLOSED" && (
          <div className="flex flex-wrap gap-2 items-end" data-testid="ca-add-evidence-form">
            <input data-testid="ca-evidence-label-input" value={evidence.label} onChange={(e) => setEvidence((v) => ({ ...v, label: e.target.value }))} placeholder={t("mgmtCorrectiveAction.evidenceLabel")} className="flex-1 min-w-[180px] border border-gray-200 rounded-lg px-3 py-1.5 text-sm" />
            <input data-testid="ca-evidence-path-input" value={evidence.storage_path} onChange={(e) => setEvidence((v) => ({ ...v, storage_path: e.target.value }))} placeholder={t("mgmtCorrectiveAction.storagePathOptional")} className="flex-1 min-w-[180px] border border-gray-200 rounded-lg px-3 py-1.5 text-sm" />
            <button data-testid="ca-add-evidence-btn" disabled={busy || !evidence.label} onClick={addEvidence} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">{t("mgmtHealthCommittee.add")}</button>
          </div>
        )}
        <div className="space-y-1">
          {(item.evidence || []).map((ev) => (
            <p key={ev.id} className="text-xs text-gray-500" data-testid={`ca-evidence-item-${ev.id}`}>{ev.label} - {t("mgmtCorrectiveAction.addedBy")} {ev.added_by} {t("mgmtHealthCommittee.at")} {ev.added_at}</p>
          ))}
          {(!item.evidence || item.evidence.length === 0) && <p className="text-xs text-gray-500" data-testid="ca-no-evidence">{t("mgmtCorrectiveAction.noEvidenceRecorded")}</p>}
        </div>
        {item.follow_up_response && <p className="text-xs text-gray-500 pt-2 border-t border-gray-100" data-testid="ca-follow-up-readonly"><span className="text-gray-500">{t("mgmtCorrectiveAction.followUpResponseLabel")}:</span> {item.follow_up_response}</p>}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">{t("mgmtHealthCommittee.auditTimeline")}</h3>
        <div className="space-y-2">
          {(item.audit_history || []).slice().reverse().map((h, idx) => (
            <div key={idx} className="text-xs border-l-2 border-gray-200 pl-3 py-1" data-testid={`ca-audit-entry-${idx}`}>
              <span className="font-semibold text-gray-700">{h.action}</span> <span className="text-gray-500">{t("mgmtHealthCommittee.by")} {h.actor} {t("mgmtHealthCommittee.at")} {h.at}</span>
              {h.notes && <p className="text-gray-500 mt-0.5">{h.notes}</p>}
            </div>
          ))}
          {(!item.audit_history || item.audit_history.length === 0) && <p className="text-xs text-gray-500" data-testid="no-ca-audit-history">{t("mgmtHealthCommittee.noAuditHistory")}</p>}
        </div>
      </div>
    </div>
  );
}
